import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiClient } from '@/lib/api';
import type {
  UserPlan,
  PlansResponse,
  AdminUpdateUserPlanRequest,
  PlanType,
} from '@omokage/shared';

/**
 * Query key factory for plan-related queries
 */
export const planKeys = {
  all: ['plans'] as const,
  lists: () => [...planKeys.all, 'list'] as const,
  userPlan: () => [...planKeys.all, 'user'] as const,
};

/**
 * Hook for fetching current user's plan information
 *
 * Returns:
 * - planType: Current plan type (free, lite, basic, pro, ultimate)
 * - billingCycle: Monthly or yearly (null for free)
 * - storageLimit: Available storage in bytes
 * - storageUsed: Used storage in bytes
 * - startedAt: Plan start timestamp
 * - expiresAt: Plan expiration timestamp (null for free or lifetime)
 *
 * @param options - Optional query options
 * @param options.enabled - Whether the query should be enabled (default: true)
 */
export function useUserPlan(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: planKeys.userPlan(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: UserPlan }>('/users/me/plan');
      return response.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 24 * 60 * 60 * 1000, // 24時間（手動同期ボタンで更新）
  });
}

/**
 * Hook for fetching all available plans
 *
 * Returns array of plan definitions with features and pricing
 */
export function usePlans() {
  return useQuery({
    queryKey: planKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get<PlansResponse>('/plans');
      return response.plans;
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7日間（プラン定義はほぼ変更されない）
  });
}

/**
 * Hook for updating a user's plan (admin only)
 *
 * Allows admins to change:
 * - planType: The plan tier
 * - billingCycle: Monthly or yearly
 * - expiresAt: When the plan expires
 *
 * @param userId - The user ID to update
 */
export function useUpdateUserPlan(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AdminUpdateUserPlanRequest) => {
      const response = await apiClient.patch<{ success: boolean; data: UserPlan }>(
        `/admin/users/${userId}/plan`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate user plan queries
      queryClient.invalidateQueries({ queryKey: planKeys.userPlan() });
      // Invalidate admin user detail to refresh storage limit
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

/**
 * Calculate storage usage percentage
 */
export function calculateStorageUsage(used: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.min(Math.round((used / limit) * 100), 100);
}

/**
 * Check if user is on free plan
 * Centralized helper for future campaign support
 */
export function isFreePlan(planType: PlanType): boolean {
  return planType === 'free';
}

/**
 * Check if a plan has a specific feature enabled
 */
export function hasPlanFeature(
  plan: UserPlan,
  featureKey: string,
  plans: PlansResponse['plans']
): boolean {
  const planDef = plans.find((p) => p.type === plan.planType);
  if (!planDef) return false;

  const feature = planDef.features.find((f) => f.key === featureKey);
  return feature?.enabled ?? false;
}

/**
 * Check if plan is expired
 */
export function isPlanExpired(expiresAt: number | null): boolean {
  if (!expiresAt) return false;
  return Date.now() / 1000 > expiresAt;
}

/**
 * Format plan expiration date
 */
export function formatPlanExpiry(expiresAt: number | null): string {
  if (!expiresAt) return 'Never';
  return new Date(expiresAt * 1000).toLocaleDateString();
}

/**
 * Get download quality label based on plan type
 * Lite+ plans support high resolution downloads, free plans get compressed
 */
export function getDownloadQualityLabel(planType: PlanType): string {
  return !isFreePlan(planType) ? 'オリジナル' : '圧縮版 (1080p)';
}

/**
 * Hook for manually refreshing plan data
 * Used by "課金状態を同期" button
 */
export function useRefreshPlan() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // refetchQueriesで確実に再取得完了を待つ
    await queryClient.refetchQueries({ queryKey: planKeys.all });
  }, [queryClient]);
}
